import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, RotateCcw, Check } from 'lucide-react';
import { compressSignatureSync } from '../../../utils/imageCompression';

/**
 * RASignatureModal - Signature capture modal for Risk Assessment
 *
 * Captures digital signatures for assessor and approver roles.
 * Based on HoursSignatureModal pattern.
 *
 * MOBILE FIX: Canvas dimensions are set dynamically to match container
 * to prevent touch offset issues on mobile devices.
 */
const RASignatureModal = ({
  isOpen,
  onClose,
  onSave,
  signatureType = 'assessor',  // 'assessor' or 'approver'
  existingSignature = null
}) => {
  const sigCanvas = useRef(null);
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [signerName, setSignerName] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 600, height: 150 });

  // Measure container and set canvas dimensions to match
  const updateCanvasDimensions = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Use actual container width, maintain aspect ratio
      const width = Math.floor(rect.width);
      const height = Math.floor(width * 0.25); // 4:1 aspect ratio
      setCanvasDimensions({ width, height });
    }
  }, []);

  // Update dimensions when modal opens and on resize
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure container is rendered
      const timer = setTimeout(updateCanvasDimensions, 50);
      window.addEventListener('resize', updateCanvasDimensions);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateCanvasDimensions);
      };
    }
  }, [isOpen, updateCanvasDimensions]);

  // Pre-fill for approver (usually Master) and load existing signature
  useEffect(() => {
    if (isOpen) {
      // Pre-fill position for approver
      if (signatureType === 'approver' && !signerPosition) {
        setSignerPosition('Master');
      }

      // Load existing signature if available
      if (existingSignature) {
        setSignerName(existingSignature.name || '');
        setSignerPosition(existingSignature.position || '');
        if (existingSignature.data && sigCanvas.current) {
          setTimeout(() => {
            sigCanvas.current.fromDataURL(existingSignature.data);
            setIsEmpty(false);
          }, 100);
        }
      }
    }
  }, [signatureType, existingSignature, isOpen]);

  const handleClear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      if (!signerName.trim()) {
        alert('Please enter your name');
        return;
      }

      // FT-129: Compress signature (resize to 300x100, white bg, JPEG 60%)
      const signatureData = {
        data: compressSignatureSync(sigCanvas),
        name: signerName.trim(),
        position: signerPosition.trim(),
        timestamp: Date.now(),
        type: signatureType
      };

      onSave(signatureData, signatureType);
      handleClear();
      setSignerName('');
      setSignerPosition('');
      onClose();
    }
  };

  if (!isOpen) return null;

  const title = signatureType === 'assessor'
    ? 'Assessor Signature'
    : 'Approval Signature';

  const subtitle = signatureType === 'assessor'
    ? 'Sign to confirm you conducted this risk assessment'
    : 'Sign to approve this risk assessment (Master/DPA)';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <input
                  type="text"
                  value={signerPosition}
                  onChange={(e) => setSignerPosition(e.target.value)}
                  placeholder={signatureType === 'approver' ? 'Master' : 'e.g., Chief Officer'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            {/* Signature Canvas - container ref for measuring actual width */}
            <div ref={containerRef} className="border-2 border-gray-300 rounded-lg bg-gray-50">
              <SignatureCanvas
                key={`${canvasDimensions.width}-${canvasDimensions.height}`}
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  width: canvasDimensions.width,
                  height: canvasDimensions.height,
                  className: 'signature-canvas',
                  style: { width: '100%', height: 'auto', touchAction: 'none' }
                }}
                onBegin={() => setIsEmpty(false)}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Sign above using your finger or stylus
            </p>

            {/* Actions */}
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={handleClear}
                disabled={isEmpty}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </button>

              <div className="space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isEmpty || !signerName.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Save Signature
                </button>
              </div>
            </div>

            {/* Compliance note */}
            <div className="mt-4 text-xs text-gray-500">
              <p>By signing, you confirm that:</p>
              <ul className="list-disc list-inside mt-1">
                <li>The risk assessment has been reviewed and is accurate</li>
                <li>All hazards have been identified and controls are appropriate</li>
                <li>Risks have been reduced to As Low As Reasonably Practicable (ALARP)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RASignatureModal;
